import { useCallback, useEffect, useState } from 'react'
import { patientService } from '../../../services/patientApi'
import { useAuth } from '../../../contexts/AuthContext'
import { formatDay } from './patientFormat'
import NoteMessage from './NoteMessage'


function dayKey(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date)
}

function groupNotes(notes) {
  const groups = []
  notes.forEach((note) => {
    const key = dayKey(note.created_at)
    const current = groups[groups.length - 1]
    if (!current || current.key !== key) groups.push({ key, label: formatDay(key), notes: [note] })
    else current.notes.push(note)
  })
  return groups
}

function NotesTab({ patientId, canManage = false }) {
  const { user } = useAuth()
  const [notes, setNotes] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async (before) => {
    const response = await patientService.notes(patientId, { scope: 'history', before })
    const payload = response.data || {}
    const page = payload.notes || []
    setHasMore(Boolean(payload.hasMore))
    setNotes((current) => (before ? [...current, ...page] : page))
  }, [patientId])

  useEffect(() => {
    let active = true
    setLoading(true)
    load()
      .catch((err) => {
        if (active) setError(err.response?.data?.message || 'Não foi possível carregar o diário')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    const refresh = () => {
      load().catch(() => {})
    }
    window.addEventListener('wfmed-shift-note', refresh)
    return () => {
      active = false
      window.removeEventListener('wfmed-shift-note', refresh)
    }
  }, [load])

  const older = notes.length ? notes[notes.length - 1].created_at : null

  return (
    <div className="patients-note-history">
      <div className="patients-section-head">
        <div>
          <h3>Diário de plantão</h3>
          <p className="patients-muted">Notas rápidas registradas neste prontuário.</p>
        </div>
      </div>
      {error && <p className="patients-alert-strip">{error}</p>}
      {loading && notes.length === 0 && <p className="patients-muted">Carregando notas...</p>}
      {!loading && notes.length === 0 && <p className="patients-muted">Nenhuma nota registrada.</p>}
      {groupNotes(notes).map((group) => (
        <section key={group.key} className="patients-note-day">
          <h4>{group.label}</h4>
          {group.notes.map((note) => (
            <NoteMessage
              key={note.id}
              note={note}
              className="patients-note-history-item"
              canChange={canManage && (user?.role === 'admin' || Number(note.author_user_id) === Number(user?.id))}
              onUpdate={async (current, body) => {
                const response = await patientService.updateNote(patientId, current.id, body)
                setNotes((list) => list.map((item) => (item.id === current.id ? response.data : item)))
              }}
              onDelete={async (current) => {
                await patientService.deleteNote(patientId, current.id)
                setNotes((list) => list.filter((item) => item.id !== current.id))
              }}
            />
          ))}
        </section>
      ))}
      {hasMore && (
        <button
          type="button"
          className="patients-btn patients-btn--ghost"
          onClick={() => load(older).catch((err) => setError(err.response?.data?.message || 'Não foi possível carregar mais notas'))}
        >
          Carregar notas anteriores
        </button>
      )}
    </div>
  )
}

export default NotesTab
